import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import DependantsDropdown from "@/features/Common/components/DependantsDropdown";
import IconLoader from "@/features/Common/components/IconLoader";
import useGetMemberships from "@/features/Common/hooks/useGetMemberships";
import {
  findMainMemberInMembership,
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
  MembershipDependent,
  MembershipDependentIdentityDocumentType,
} from "@/services/core-api-adapter";
import { useGlobalStore } from "@/store";
import { defaultFullscreenPageStyling, styleInputDefault } from "@/theme";
import { convertPxToRem, replaceCentsFromAmount } from "@/utils";
import {
  Autocomplete,
  Box,
  Divider,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

interface DocMedsDeliveryInfoProps {
  memberProfile: MemberProfile;
  onBackButtonClick?: () => void;
  onNextButtonClick: (selectedMember: any) => void;
  dependants: MembershipDependent[];
}

export default function DocMedsDeliveryInfo({
  memberProfile,
  onBackButtonClick,
  onNextButtonClick,
  dependants,
}: DocMedsDeliveryInfoProps) {
  const { t } = useTranslation();
  const cannotDeliverToAddress = false;
  const [selectedMember, setSelectedMember] =
    useState<MembershipDependent | null>(null);
  const { state } = useGlobalStore();
  const [dependentsToShow, setDependentsToShow] =
    useState<MembershipDependent[]>(dependants);

  const directToDoctorPaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  const { firstMembership, isMembershipsLoading, membershipsFetchError } =
    useGetMemberships();

  useEffect(() => {
    const mainMember = findMainMemberInMembership(
      firstMembership,
      state.currentUser.memberId
    );
    const updatedDependants = [...dependentsToShow];
    const mainMemberToAdd = {
      memberId: state.currentUser.memberId,
      firstName: state.currentUser.firstName,
      lastName: state.currentUser.lastName,
      identityDocumentType: mainMember?.memberDetails
        .identityDocumentType as unknown as MembershipDependentIdentityDocumentType,
      identityDocumentValue: mainMember?.memberDetails.identityDocumentValue,
      sexAtBirth: "",
      dateOfBirth: mainMember?.memberDetails.dateOfBirth,
      identityDocumentCountryOfIssue: "",
      status: "active",
    };

    if (
      updatedDependants.find(
        (member) => member.memberId === mainMemberToAdd.memberId
      ) === undefined
    ) {
      updatedDependants.push(mainMemberToAdd);
    }

    setDependentsToShow(updatedDependants);
  }, [isMembershipsLoading, membershipsFetchError]);

  function handleSelectedMember(selectedMemberMembershipID: any) {
    const getSelectedMember = dependentsToShow.find(
      (member: MembershipDependent) =>
        member.memberId === selectedMemberMembershipID
    );
    setSelectedMember(getSelectedMember as any);
  }

  return (
    <Stack sx={{ ...defaultFullscreenPageStyling }} spacing={1}>
      <CircledBackButton showLabel onClick={onBackButtonClick} />
      <Stack spacing={4}>
        <Stack alignItems="center">
          <IconLoader
            icon="ChatWithMapAndUnuIcon"
            sx={{
              fontSize: convertPxToRem(80),
            }}
          />
          <Typography component="h1" variant="h2">
            {t("DocMedsDeliveryInfo.title")}
          </Typography>
        </Stack>
        <Stack spacing={2}>
          <Typography component="h2" variant="h3" fontWeight="bold">
            {t("DocMedsDeliveryInfo.subtitle")}
          </Typography>
          <Stack
            flexDirection="row"
            gap={2}
            sx={{ backgroundColor: "accent2.100", padding: 2, borderRadius: 2 }}
          >
            <IconLoader
              icon="InformationSymbolFilledCircleIcon"
              color="primary"
            />
            <Typography component="p" variant="body2">
              <Trans i18nKey="DocMedsDeliveryInfo.info" />
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <InputLabel htmlFor={t("DocMedsDeliveryInfo.addressInput.label")}>
            <Typography variant="body2" color="neutral.main" fontWeight="bold">
              {t("DocMedsDeliveryInfo.addressInput.label")}
            </Typography>
          </InputLabel>
          <Autocomplete
            id={t("DocMedsDeliveryInfo.addressInput.label")}
            freeSolo
            options={["An address"]}
            sx={{ backgroundColor: "background.paper" }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  input: {
                    ...styleInputDefault,
                    fontSize: `${convertPxToRem(14)} !important`,
                  },
                }}
                placeholder={t("DocMedsDeliveryInfo.addressInput.placeholder")}
              />
            )}
          />
        </Stack>
        {cannotDeliverToAddress && (
          <Stack
            sx={{ p: 2, backgroundColor: "background.paper", borderRadius: 2 }}
            spacing={1}
          >
            <Typography variant="body1" fontWeight="bold">
              {t("DocMedsDeliveryInfo.cannotDeliver.title")}
            </Typography>
            <Typography variant="body2">
              <Trans i18nKey="DocMedsDeliveryInfo.cannotDeliver.body" />
            </Typography>
            <Box
              sx={{
                p: 1,
                backgroundColor: "primary.light",
                borderRadius: 2,
                width: "fit-content",
              }}
            >
              <Typography
                variant="body1"
                color="primary.contrastText"
                fontWeight="bold"
              >
                <Trans
                  i18nKey="DocMedsDeliveryInfo.cannotDeliver.price"
                  values={{
                    price: replaceCentsFromAmount(
                      directToDoctorPaymentRates?.amountFormatted || ""
                    ),
                  }}
                />
              </Typography>
            </Box>
          </Stack>
        )}

        {!cannotDeliverToAddress && (
          <>
            <Divider />
            <Stack spacing={1}>
              <InputLabel
                htmlFor={t("DocMedsDeliveryInfo.dependantSelection.ariaLabel")}
              >
                <Typography
                  variant="body2"
                  color="neutral.main"
                  fontWeight="bold"
                >
                  {t("DocMedsDeliveryInfo.dependantSelection.label")}
                </Typography>
              </InputLabel>
              <DependantsDropdown
                setSelectedMembershipId={handleSelectedMember}
                dependants={dependentsToShow}
                selectMemberLabel=""
                selectMemberPlaceholder={t(
                  "MedicalAssistanceDependentSelection.selectMemberPlaceholder"
                )}
              />
            </Stack>
          </>
        )}

        <Stack>
          {cannotDeliverToAddress ? (
            <Stack direction="row" gap={2}>
              <ButtonWithAnalytics
                fullWidth
                page="DocMedsDeliveryInfo"
                intent="navigational"
                text={t("common.goHomeButton")}
                variant="outlined"
              >
                {t("common.goHomeButton")}
              </ButtonWithAnalytics>
              <ButtonWithAnalytics
                fullWidth
                page="DocMedsDeliveryInfo"
                intent="navigational"
                text={t("common.yesPlease")}
              >
                {t("common.yesPlease")}
              </ButtonWithAnalytics>
            </Stack>
          ) : (
            <ButtonWithAnalytics
              page="DocMedsDeliveryInfo"
              intent="navigational"
              text={t("common.nextButton")}
              onClick={() => {
                onNextButtonClick(selectedMember);
              }}
            >
              {t("common.nextButton")}
            </ButtonWithAnalytics>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
